import React from 'react';
import { ExternalLink, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Tooltip, TooltipPanel, TooltipTrigger } from '@clay-run/glaze/tooltip';

/**
 * Shared link + out-link-icon primitives for the Flow surface. Built clean in
 * core (no action-view import — mini-apps can't be imported from core, and the
 * Flow contact/account views are the canonical owners of these).
 */

export const SFDC_BASE = 'https://clay.lightning.force.com';

/** Every SFDC object type the Flow surface links out to. Shared by
 *  `sfdcRecordUrl` and `SfdcCloudIcon` so the two can never drift apart. */
export type SfdcRecordKind =
  | 'Contact'
  | 'Account'
  | 'Opportunity'
  | 'Signal_Action__c'
  | 'Task'
  | 'Event'
  | 'Events_and_Signals__c'
  | 'Workspace__c'
  | 'Workspace_User_Relationship__c'
  // QA round 7, U7 (Playbooks item 13) — a Lead-sourced campaign member
  // (`CampaignMember.lead_id`) has no Contact row, so the member-detail
  // drawer needs a Lead record kind to link out to instead of Contact.
  | 'Lead';

export function sfdcRecordUrl(kind: SfdcRecordKind, id: string): string {
  return `${SFDC_BASE}/lightning/r/${kind}/${id}/view`;
}

/** Gong workspace + host for the Engage to-dos deep-link below. */
export const GONG_WORKSPACE_ID = '2151574214928240805';
export const GONG_BASE = 'https://us-84989.app.gong.io';

/**
 * Gong Engage to-dos deep-link, filtered to one contact (Spencer's exact
 * target URL, confirmed 2026-08-20 against a real successful live Gong
 * send): `workspace-id` as a top-level query param, plus `activeTab: 'toDos'`
 * inside the `contact-params` JSON, alongside `type: 'ENGAGE'` and
 * `contactCrmId` (+ `accountCrmId` when known).
 *
 * DELIBERATE DUPLICATE of `mini-apps/action-view/utils.ts`'s `gongEngageUrl`
 * — core UI cannot import mini-app code (mini-apps/CLAUDE.md lane 1), so this
 * is a hand-copied twin, same as `state-links.ts`'s own comment on this
 * relationship. Keep the two in sync by hand if either shape ever changes:
 * same `GONG_BASE`/`GONG_WORKSPACE_ID`, same query-param shape, same
 * contact-params JSON+encodeURIComponent encoding.
 */
export function gongEngageUrl(
  contactId: string | null | undefined,
  accountCrmId: string | null | undefined,
): string {
  if (!contactId) return `${GONG_BASE}/todos`;
  const params: Record<string, string> = {
    type: 'ENGAGE',
    contactCrmId: contactId,
  };
  if (accountCrmId) params.accountCrmId = accountCrmId;
  params.activeTab = 'toDos';
  const contactParams = encodeURIComponent(JSON.stringify(params));
  return `${GONG_BASE}/todos?workspace-id=${GONG_WORKSPACE_ID}&tab=ALL&quickFilter=%7B%7D&contact-params=${contactParams}`;
}

/**
 * Normalize an href for a rendered `<a>`: pass http(s) through, pass a
 * single-leading-slash internal path through (U15/D6 — a
 * `terra_signals.source_link` of `/flow/requests/ei-<id>` is a first-class
 * in-app route, not a scheme-less host: `https://` would break it into
 * `https:///flow/...`), prepend `https://` to a scheme-less host, and REJECT
 * any other scheme (returns null). These links render values that cross a
 * trust boundary (SFDC HYPERLINK() formulas, DB columns), and React does not
 * sanitize `href` — so a `javascript:`/`data:`/`vbscript:` URL would
 * otherwise execute on click. Callers drop the link when this returns null.
 *
 * The internal-path check is a SINGLE leading slash only (negative lookahead
 * excludes a second slash) — `//evil.com` is protocol-relative-URL syntax,
 * not an app-internal path, so it deliberately falls through to the
 * scheme-less-host branch exactly as it did before this change.
 */
export function withProtocol(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (isInternalHref(trimmed)) return trimmed;
  // Any other explicit scheme (javascript:, data:, mailto:, …) is unsafe/unsupported here.
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return null;
  return `https://${trimmed}`;
}

/**
 * True for a single-leading-slash in-app path (`/flow/requests/ei-1`) —
 * false for a protocol-relative `//host` string, an http(s) URL, or any
 * other scheme. Shared by `withProtocol` and every component below that
 * needs to pick the in-app `<Link>` branch vs. the external `<a target
 * ="_blank">` branch for the same href.
 */
export function isInternalHref(href: string): boolean {
  return /^\/(?!\/)/.test(href);
}

/**
 * SFDC's domain field is a HYPERLINK() formula, so its value arrives as an HTML
 * anchor string (`<a href="https://x.com" target="_blank">x.com</a>`), not a
 * bare URL. Extract the href + a clean hostname label. Falls back gracefully
 * for a raw URL or a plain hostname.
 */
export function parseDomainLink(raw: string | null | undefined): { href: string; label: string } | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const hrefMatch = trimmed.match(/href\s*=\s*["']([^"']+)["']/i);
  const rawHref = hrefMatch ? hrefMatch[1] : trimmed.replace(/<[^>]*>/g, '').trim();
  if (!rawHref) return null;

  const href = withProtocol(rawHref);
  if (!href) return null; // non-http(s) scheme (e.g. javascript:) → no link
  let label = rawHref;
  try {
    label = new URL(href).hostname.replace(/^www\./, '');
  } catch {
    const textMatch = trimmed.match(/>([^<]+)</);
    if (textMatch) label = textMatch[1].trim();
  }
  return { href, label };
}

/**
 * Compact, clickable link for table cells — a short label + a small out-link
 * glyph. Stops row-click propagation so opening the link never toggles the
 * row. Renders an em dash when there's no url.
 *
 * Internal path (U15/D6, e.g. `terra_signals.source_link =
 * '/flow/requests/ei-1'`): an in-app react-router `<Link>`, same tab, no
 * `ExternalLink` glyph (nothing to signal "leaves the app"). External URL:
 * unchanged — `<a target="_blank">` + the `ExternalLink` glyph.
 */
export function CompactUrlLink({ url, label }: { url: string | null | undefined; label: string }) {
  const href = url ? withProtocol(url) : null;
  if (!href) return <span className="text-content-faint dark:text-nightshade-600">—</span>;
  if (isInternalHref(href)) {
    return (
      <Link
        to={href} onClick={e => e.stopPropagation()}
        title={url ?? undefined}
        className="inline-flex items-center gap-0.5 text-content-action dark:text-blueberry-400 hover:text-content-action-hover dark:hover:text-blueberry-300 hover:underline"
      >
        {label}
      </Link>
    );
  }
  return (
    <a
      href={href} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
      title={url ?? undefined}
      className="inline-flex items-center gap-0.5 text-content-action dark:text-blueberry-400 hover:text-content-action-hover dark:hover:text-blueberry-300 hover:underline"
    >
      {label}
      <ExternalLink className="w-3 h-3 shrink-0" />
    </a>
  );
}

export const ICON_LINK_CLASS =
  'text-content-action dark:text-blueberry-400 hover:text-content-action-hover dark:hover:text-blueberry-300';

/**
 * Glaze tooltip for an icon-only out-link. The label doubles as the link's
 * `aria-label`, so it replaces the native `title` (which would show a second,
 * unstyled tooltip on top of this one).
 */
function IconLinkTip({ label, children }: { label: string; children: React.ReactElement }) {
  return (
    <Tooltip>
      <TooltipTrigger>{children}</TooltipTrigger>
      <TooltipPanel>{label}</TooltipPanel>
    </Tooltip>
  );
}

const SOURCE_OPEN_BUTTON_CLASS =
  'mt-2 inline-flex items-center gap-1 text-[11.5px] font-medium text-content-action dark:text-blueberry-400 bg-blueberry-50 dark:bg-blueberry-900/20 border border-blueberry-100 dark:border-blueberry-900/40 rounded-md px-2 py-1 hover:bg-blueberry-100 dark:hover:bg-blueberry-900/30 hover:text-content-action-hover dark:hover:text-blueberry-300';

/**
 * A clear, labeled out-link button for an expanded accordion detail —
 * mirrors `AccountTimeline.tsx`'s local `SfdcOpenButton` exactly (same
 * classes), but takes a raw URL + label rather than a `SfdcRecordKind`+id
 * pair, since not every caller has one to reconstruct a Salesforce URL from
 * (e.g. `terra_signals.source_link` is already a built URL). Renders nothing
 * when there's no url — callers gate on that themselves if they need to know.
 *
 * Internal path (U15/D6): an in-app react-router `<Link>`, no `target=
 * "_blank"`, default label 'Open in Terra →'. External URL: unchanged.
 */
export function SourceOpenButton({ url, label }: { url: string | null | undefined; label?: string }) {
  const href = url ? withProtocol(url) : null;
  if (!href) return null;
  if (isInternalHref(href)) {
    return (
      <Link
        to={href}
        onClick={e => e.stopPropagation()}
        className={SOURCE_OPEN_BUTTON_CLASS}
      >
        {label ?? 'Open in Terra →'}
      </Link>
    );
  }
  return (
    <a
      href={href}
      target="_blank" rel="noreferrer"
      onClick={e => e.stopPropagation()}
      className={SOURCE_OPEN_BUTTON_CLASS}
    >
      {label ?? 'Open in Salesforce ↗'}
    </a>
  );
}

/** The Salesforce cloud glyph itself, shared by the record and raw-URL cloud links. */
function SfdcCloudGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19.5 14.8c0-1.2-.5-2.3-1.3-3.1.2-.5.3-1 .3-1.6 0-2.2-1.8-4-4-4-.7 0-1.4.2-2 .5a4.5 4.5 0 00-7.5 2c-.3 0-.5-.1-.8-.1-2 0-3.7 1.7-3.7 3.7 0 2 1.7 3.7 3.7 3.7h1.2c.5 1.1 1.7 1.8 3 1.8.9 0 1.7-.3 2.3-.9.6.6 1.4.9 2.3.9 1.3 0 2.5-.7 3-1.8h.3c1.7 0 3.2-1.4 3.2-3.1z" />
    </svg>
  );
}

/** Salesforce "cloud" out-link icon (mirrors action-view's SfdcIcon). */
export function SfdcCloudIcon({
  kind, id,
}: {
  kind: SfdcRecordKind;
  id: string | null | undefined;
}) {
  if (!id) return null;
  return (
    <IconLinkTip label="Open in Salesforce">
      <a
        href={sfdcRecordUrl(kind, id)} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
        className={ICON_LINK_CLASS} aria-label="Open in Salesforce"
      >
        <SfdcCloudGlyph />
      </a>
    </IconLinkTip>
  );
}

/**
 * Cloud out-link icon for a raw URL rather than a `kind`+`id` pair — mirrors
 * `SfdcCloudIcon`'s exact SVG/style (every populated `terra_signals.source_link`
 * today is a Salesforce record link) but takes the already-built URL, same
 * raw-`url`-prop precedent as `GongCallIcon`/`LinkedInIcon` below. Used inline
 * in a row header (e.g. `ContactTimeline`'s action rows, 2026-09-23) where the
 * row only has a URL, not a `SfdcRecordKind`+id pair to reconstruct one from.
 *
 * Internal path (U15/D6, e.g. `/flow/requests/ei-1`): an in-app react-router
 * `<Link>`, no `target="_blank"`, a neutral `ArrowUpRight` glyph (not
 * `ExternalLink` and not this Salesforce cloud — neither implies "leaves the
 * app" or "this is Salesforce"), title 'Open in Terra'. External URL:
 * unchanged.
 */
export function SourceLinkIcon({ url }: { url: string | null | undefined }) {
  const href = url ? withProtocol(url) : null;
  if (!href) return null;
  if (isInternalHref(href)) {
    return (
      <IconLinkTip label="Open in Terra">
        <Link
          to={href}
          onClick={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
          className={ICON_LINK_CLASS} aria-label="Open in Terra"
        >
          <ArrowUpRight className="w-[15px] h-[15px]" />
        </Link>
      </IconLinkTip>
    );
  }
  return (
    <IconLinkTip label="Open in Salesforce">
      <a
        href={href} target="_blank" rel="noreferrer"
        onClick={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
        className={ICON_LINK_CLASS} aria-label="Open in Salesforce"
      >
        <SfdcCloudGlyph />
      </a>
    </IconLinkTip>
  );
}

const GONG_ICON_LINK_CLASS =
  'text-ube-600 dark:text-ube-400 hover:text-ube-700 dark:hover:text-ube-300';

/**
 * Gong call out-link icon — a phone glyph, deliberately styled in `ube`
 * (violet, approximating Gong's brand color) rather than the blue
 * `ICON_LINK_CLASS` every SFDC/LinkedIn icon in this file uses, so a rep can
 * tell "this is Gong, not Salesforce" at a glance (the exact ask behind this
 * component: "any relevant gong or sfdc links").
 *
 * Takes the already-built URL as a prop — mirrors `CompactUrlLink`'s and
 * `LinkedInIcon`'s raw-`url` shape — rather than importing a `gongCallUrl`
 * helper itself. `AccountDetailPanels.tsx` keeps its own local `gongCallUrl`
 * helper intentionally (see the comment above that helper): Flow's Gong
 * URL-building stays at its one call site, so this shared, multi-surface file
 * never needs to know Gong's URL shape.
 */
export function GongCallIcon({ url }: { url: string | null | undefined }) {
  if (!url) return null;
  return (
    <IconLinkTip label="Open in Gong">
      <a
        href={url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
        className={GONG_ICON_LINK_CLASS} aria-label="Open in Gong"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
          <path d="M6.6 10.8c1.4 2.8 3.7 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.5 2.3.8 3.6.9.6.1 1 .5 1 1.1v3.3c0 .6-.4 1-1 1C10.6 21.3 2.7 13.4 2.7 4c0-.6.4-1 1-1H7c.6 0 1 .4 1.1 1 .1 1.3.4 2.5.9 3.6.2.4.1.8-.2 1.1l-2.2 2.1z" />
        </svg>
      </a>
    </IconLinkTip>
  );
}

/**
 * Compact "in" pill link for a table cell — extracted from
 * `ContactFlowTable.tsx`'s `linkedin` renderer (QA batch 1, D-F) so
 * `AccountFlowTable.tsx` and the Signals table can share the exact same
 * markup. `withProtocol` completes a scheme-less host (Contacts' copy always
 * had this; Accounts' own inline renderer did NOT — 47,585 scheme-less
 * `salesforce_account.linkedin` values on prod-mirror resolved relative to
 * the Terra origin before this). Renders an em dash for null/blank/unsafe.
 */
export function LinkedInCell({ url }: { url: string | null | undefined }) {
  const href = url ? withProtocol(url) : null;
  if (!href) return <span className="text-content-faint dark:text-nightshade-600">—</span>;
  return (
    <IconLinkTip label="View on LinkedIn">
      <a
        href={href} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
        aria-label="View on LinkedIn"
        className="inline-block px-1 rounded text-[10px] font-bold leading-4 bg-bg-blue-light text-content-action dark:text-blueberry-400 hover:text-content-action-hover dark:hover:text-blueberry-300"
      >
        in
      </a>
    </IconLinkTip>
  );
}

/**
 * Clickable domain cell — extracted from `AccountFlowTable.tsx`'s
 * `email_domain_link` renderer (QA batch 1, D-F) so the Signals table can
 * share it. `parseDomainLink` unwraps SFDC's `HYPERLINK()` formula string
 * into an href + clean hostname label. Renders an em dash for null.
 */
export function DomainLinkCell({ raw }: { raw: string | null | undefined }) {
  const parsed = parseDomainLink(raw);
  if (!parsed) return <span className="text-content-faint dark:text-nightshade-600">—</span>;
  return (
    <a
      href={parsed.href} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
      className="text-content-action dark:text-blueberry-400 hover:underline"
    >
      {parsed.label}
    </a>
  );
}

/** LinkedIn out-link icon (mirrors action-view's LinkedInIcon). */
export function LinkedInIcon({ url }: { url: string | null | undefined }) {
  const href = url ? withProtocol(url) : null;
  if (!href) return null;
  return (
    <IconLinkTip label="View on LinkedIn">
      <a
        href={href} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
        className={ICON_LINK_CLASS} aria-label="View on LinkedIn"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z" />
        </svg>
      </a>
    </IconLinkTip>
  );
}
